import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, FileText } from "lucide-react";
import { Reveal, MaskLine } from "@/components/Reveal";
import {
  CAPABILITY_OVERVIEW,
  CAPABILITIES,
  COMPETENCIES,
  MANIFESTO,
  IMAGES,
} from "@/data/content";

export default function Capabilities() {
  return (
    <div data-testid="capabilities-page" className="pt-[72px]">
      {/* Header */}
      <section
        data-testid="capabilities-header"
        className="relative overflow-hidden grain border-b border-white/10"
        style={{
          backgroundImage: `linear-gradient(rgba(10,10,10,0.84), rgba(10,10,10,0.94)), url(${IMAGES.blueprint})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 md:py-32 relative z-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-6">
            Capability Statement
          </p>
          <h1 className="font-heading font-black uppercase tracking-tighter text-5xl sm:text-6xl md:text-7xl leading-[0.95] max-w-4xl">
            <MaskLine delay={0.1}>Integrated</MaskLine>
            <MaskLine delay={0.22}>
              <span className="text-blue-500">capabilities.</span>
            </MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="mt-8 max-w-3xl text-lg text-white/65 font-light leading-relaxed"
          >
            {CAPABILITY_OVERVIEW}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.8 }}
            className="mt-8"
          >
            <a
              href="/capability-statement"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                window.open("/capability-statement", "_blank", "noopener,noreferrer");
              }}
              data-testid="capabilities-pdf-button"
              className="group inline-flex items-center gap-2 px-7 py-3.5 bg-[#0055FF] hover:bg-[#0044CC] text-white font-bold uppercase tracking-wide text-sm transition-transform hover:scale-[1.04]"
            >
              <FileText size={16} />
              Capabilities PDF
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Capability chapters */}
      <section data-testid="capabilities-chapters" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 md:py-28">
        <div className="space-y-px">
          {CAPABILITIES.map((c, i) => (
            <Reveal
              key={c.num}
              delay={i * 0.05}
              className="py-10 border-t border-white/10 group"
              data-testid={`capability-${i}`}
            >
              <div>
                <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight mb-4">{c.title}</h2>
                <p className="text-white/55 text-base md:text-lg leading-relaxed max-w-3xl font-light">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How we operate */}
      <section data-testid="how-we-operate-section" className="relative bg-[#0A0A0A] border-y border-white/10 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-16">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-4">How we operate</p>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl md:text-5xl tracking-tight">
              An integrated partner from concept to commissioning.
            </h2>
          </Reveal>
          <div className="space-y-px">
            {MANIFESTO.map((m, i) => (
              <Reveal
                key={m.num}
                delay={i * 0.05}
                className="py-10 border-t border-white/10 group"
                data-testid={`manifesto-${i}`}
              >
                <div>
                  <h3 className="font-heading font-bold text-2xl md:text-3xl tracking-tight mb-4">{m.title}</h3>
                  <p className="text-white/55 text-base md:text-lg leading-relaxed max-w-3xl font-light">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Core competencies */}
      <section data-testid="capabilities-competencies" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <Reveal className="mb-12 max-w-2xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-4">Core Competencies</p>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl md:text-5xl tracking-tight">
              What we deliver, end to end.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-px">
            {COMPETENCIES.map((c, i) => (
              <Reveal
                key={c}
                delay={(i % 3) * 0.05}
                className="flex items-center gap-4 py-4 border-t border-white/10 group"
                data-testid={`cap-competency-${i}`}
              >
                <ShieldCheck size={16} className="text-blue-500 shrink-0" />
                <span className="text-white/70 group-hover:text-white transition-colors font-light">{c}</span>
              </Reveal>
            ))}
          </div>
      </section>

      {/* CTA */}
      <section data-testid="capabilities-cta" className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 text-center">
          <Reveal>
            <h2 className="font-heading font-black uppercase tracking-tighter text-3xl sm:text-4xl md:text-5xl leading-[0.95] max-w-3xl mx-auto">
              Ready to put our capabilities to work? <span className="text-blue-500">Let&apos;s talk.</span>
            </h2>
            <Link
              to="/contact"
              data-testid="capabilities-cta-button"
              className="inline-flex items-center gap-2 mt-8 px-8 py-4 bg-[#0055FF] hover:bg-[#0044CC] text-white font-bold uppercase tracking-wide text-sm transition-transform hover:scale-[1.04]"
            >
              Start a Project <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
